import React, { ReactChild, ReactNode } from 'react';

import { QuestionMarkCircleIcon } from '@heroicons/react/outline';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { SelectChangeEvent } from '@mui/material';
import { FormControl, MenuItem, Select, Tooltip } from '@mui/material';
import { useField } from 'formik';

import FieldError from '@components/forms/FieldError';

import { isNotBlank } from '@util/StringUtil';
import { isDefined } from '@util/TypeGuards';

export type ChoiceItem = { label?: ReactNode; value: string | number };

type Props = {
    disabled?: boolean;
    items: ChoiceItem[];
    label: ReactNode;
    labelClassName?: string;
    loading?: boolean;
    name: string;
    onChange?: (event: SelectChangeEvent<any>, child?: ReactNode) => void;
    placeholder?: string;
    tooltip?: { title: NonNullable<ReactNode>; href?: string | null; icon?: ReactChild | null } | null;
    value?: string | number | null;
};
const SimpleSelectField = ({
    disabled,
    items,
    label,
    labelClassName,
    loading = false,
    name,
    onChange,
    placeholder,
    tooltip,
    value,
}: Props) => {
    const [inputProps, meta] = useField(name);
    const touched = meta.touched;
    const error = touched ? meta.error : undefined;

    const LOADING_VALUE = '--SIMPLE-SELECT-FIELD-LOADING--';
    // PLACEHOLDER_VALUE is removed as we'll use displayEmpty and '' for value
    const NO_OPTIONS_VALUE = '--SIMPLE-SELECT-FIELD-NO-OPTIONS--';

    let currentSingleValue = value ?? inputProps.value;
    if (Array.isArray(currentSingleValue)) {
        // If Formik value is an array, take the first element for single select.
        // This handles cases where the field might be mistyped as an array for a single-select component.
        currentSingleValue = currentSingleValue.length > 0 ? currentSingleValue[0] : undefined;
    }

    // Ensure the value passed to MUI Select is always a string.
    // Default to empty string if currentSingleValue is null/undefined.
    const fieldValue =
        currentSingleValue === null || currentSingleValue === undefined ? '' : String(currentSingleValue);

    let $tooltip: ReactNode | null = null;
    if (isDefined(tooltip)) {
        const $icon = tooltip.icon ?? <QuestionMarkCircleIcon className="h-4 w-4" />;
        const $child = isNotBlank(tooltip.href) ? (
            <a href={tooltip.href} target="_blank" rel="nofollow noreferrer">
                {$icon}
            </a>
        ) : (
            $icon
        );
        $tooltip = (
            <Tooltip title={tooltip.title} placement="right" arrow>
                <span>{$child}</span>
            </Tooltip>
        );
    }

    return (
        <div className="form-field no-margin">
            <span className="field-label flex items-center space-x-2">
                <span className={labelClassName}>{label}</span>
                {$tooltip}
            </span>
            <FormControl variant="outlined" fullWidth error={!!error}>
                <Select
                    IconComponent={KeyboardArrowDownRoundedIcon}
                    margin="dense"
                    {...inputProps}
                    displayEmpty={isNotBlank(placeholder)} // Use displayEmpty prop
                    onChange={onChange ?? inputProps.onChange}
                    value={fieldValue} // Will be '' if empty
                    renderValue={(selectedVal) => {
                        if (selectedVal === '' && isNotBlank(placeholder)) {
                            return <span className="text-default text-opacity-50">{placeholder}</span>;
                        }
                        const selection = items.find((item) => `${item.value}` === `${selectedVal}`);
                        return selection?.label ?? selection?.value ?? selectedVal;
                    }}
                    disabled={disabled}
                >
                    {loading && (
                        <MenuItem key="loading" disabled value={LOADING_VALUE}>
                            Loading...
                        </MenuItem>
                    )}
                    {/* Placeholder MenuItem is no longer needed with displayEmpty */}
                    {!loading &&
                        items?.map((option, index) => {
                            return (
                                <MenuItem key={`item-${index}`} value={option.value}>
                                    {option.label ?? option.value}
                                </MenuItem>
                            );
                        })}

                    {!loading && !items?.length && (
                        <MenuItem key="no_options" value={NO_OPTIONS_VALUE} disabled>
                            No options found
                        </MenuItem>
                    )}
                </Select>
                {error && <FieldError>{error}</FieldError>}
            </FormControl>
        </div>
    );
};

export default SimpleSelectField;
